import React, {useCallback, useEffect, useState} from "react";
import {api_data} from "../../services/api_config";
import {useMyContext} from "../../store/ContextApi";
import {useNavigate, useParams} from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiSave,
    FiCheck,
    FiAlertCircle,
    FiUserX,
    FiSend,
    FiChevronDown,
} from "react-icons/fi";

const today = new Date();

const getMonthTitle = (timesheets) => {
    if (timesheets && timesheets.length > 0) {
        const date = new Date(timesheets[0].workSegmentDate);
        return `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
    }
    return `${today.toLocaleString('default', { month: 'long' })} ${today.getFullYear()}`;
};

const formatEntryDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    });

const groupTimesheetsByProjectAndWeek = (timesheets) => {
    const groupedData = {};

    timesheets.forEach(timesheet => {
        const projectId = timesheet.projectid;

        const date = new Date(timesheet.workSegmentDate);
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        const weekStart = new Date(date.setDate(diff));
        const weekKey = weekStart.toISOString().split('T')[0];

        if (!groupedData[projectId]) {
            groupedData[projectId] = {
                projectname: timesheet.projectname,
                weeks: {},
                totalHours: 0
            };
        }

        if (!groupedData[projectId].weeks[weekKey]) {
            groupedData[projectId].weeks[weekKey] = {
                entries: [],
                weekTotal: 0
            };
        }

        groupedData[projectId].weeks[weekKey].entries.push(timesheet);
        groupedData[projectId].weeks[weekKey].weekTotal += timesheet.hoursWorked;
        groupedData[projectId].totalHours += timesheet.hoursWorked;
    });

    return groupedData;
};

function updateTimesheetGroups(monthlyTimesheets, setMonthlyTimesheet, setGroupedTimesheets, setProjects) {
    const grouped = groupTimesheetsByProjectAndWeek(monthlyTimesheets.timesheets);
    setMonthlyTimesheet(monthlyTimesheets);
    setGroupedTimesheets(grouped);

    const projectsList = Object.keys(grouped).map(projectId => ({
        id: projectId,
        name: grouped[projectId].projectname
    }));
    setProjects(projectsList);
    return projectsList;
}

const isDraftStatus = (status) => !status || status === "";

const TimesheetRow = React.memo(({ entry, index, projectId, weekKey, isEditing, onChange }) => {
    const hours = entry.hoursWorked ?? 0;
    const hasHours = hours > 0;

    return (
        <tr className="timesheet-row-compact hover:bg-theme-row-hover">
            <td className="pl-4 pr-3">
                <span className="text-sm text-theme-text">{formatEntryDate(entry.workSegmentDate)}</span>
            </td>
            <td className="px-3 text-right">
                {isEditing ? (
                    <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={hours}
                        onChange={(e) => onChange(projectId, weekKey, index, 'hoursWorked', e.target.value)}
                        className="input-hours"
                        aria-label={`Hours for ${formatEntryDate(entry.workSegmentDate)}`}
                    />
                ) : (
                    <span className={`hours-chip ${hasHours ? 'hours-chip-filled' : 'hours-chip-empty'}`}>
                        {hours}h
                    </span>
                )}
            </td>
        </tr>
    );
});

const NotRegistered = () => (
    <div className="page-shell flex items-center justify-center px-4">
        <div className="surface-card max-w-md w-full p-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/10">
                <FiUserX className="h-8 w-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold text-theme-text mb-2">Not Registered Yet</h2>
            <p className="text-theme-text-secondary mb-1">
                Your account is not yet set up as an employee in the payroll system.
            </p>
            <p className="text-theme-muted text-sm">
                Please wait until HR completes your employee setup. Once done, your timesheets will appear here automatically.
            </p>
        </div>
    </div>
);

const TimesheetProjectView = () => {
    const { payrollCycleId } = useParams();
    const [groupedTimesheets, setGroupedTimesheets] = useState({});
    const [selectedProject, setSelectedProject] = useState('all');
    const [projects, setProjects] = useState([]);
    const [editableTimesheets, setEditableTimesheets] = useState({});
    const [notRegistered, setNotRegistered] = useState(false);
    const [loading, setLoading] = useState(true);
    const { logout } = useMyContext();
    const navigate = useNavigate();
    const [monthlyTimesheet, setMonthlyTimesheet] = useState({
        status: "", id: 0, projectIds: [], payrollId: 0, timesheetId: 0, timesheets: editableTimesheets
    });
    const [isEditing, setIsEditing] = useState(false);

    const isDraft = isDraftStatus(monthlyTimesheet.status);
    const canEdit = isDraft;

    useEffect(() => {
        setSelectedProject('all');
        setProjects([]);
        setGroupedTimesheets({});
        setIsEditing(false);
        fetchAndGroupTimesheets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payrollCycleId]);

    useEffect(() => {
        if (selectedProject === 'all' && projects.length > 0) {
            setSelectedProject(projects[0].id);
        }
    }, [projects, selectedProject]);

    useEffect(() => {
        if (canEdit && Object.keys(groupedTimesheets).length > 0) {
            setIsEditing(true);
            setEditableTimesheets(groupedTimesheets);
        }
    }, [canEdit, groupedTimesheets]);

    const handleUpdate = async (data) => {
        try {
            return await api_data.put(`/ts/api/timesheets`, data);
        } catch (error) {
            console.error("Error saving timesheet:", error);
            throw error;
        }
    };

    const handleSaveAll = async () => {
        const updatedTimesheets = Object.values(editableTimesheets).flatMap(project =>
            Object.values(project.weeks).flatMap(week => week.entries)
        );
        const dataToSave = { ...monthlyTimesheet, timesheets: updatedTimesheets };
        const previousState = { ...monthlyTimesheet };
        try {
            const response = await handleUpdate(dataToSave);
            const projectsList = updateTimesheetGroups(response.data, setMonthlyTimesheet, setGroupedTimesheets, setProjects);
            toast.success('Timesheet saved');

            if (selectedProject === 'all' && projectsList.length > 0) {
                setSelectedProject(projectsList[0].id);
            }
        } catch (error) {
            console.error("Error saving timesheets:", error);
            updateTimesheetGroups(previousState, setMonthlyTimesheet, setGroupedTimesheets, setProjects);

            if (error.response?.status === 400) {
                toast.error('Timesheet already submitted/approved');
            } else if (error.response?.status === 401) {
                logout();
                navigate("/login");
            }
        }
    };

    const handleInputChange = useCallback((projectId, weekKey, index, field, value) => {
        setEditableTimesheets(prevState => {
            const entries = prevState[projectId].weeks[weekKey].entries;
            const updatedEntry = { ...entries[index], [field]: field === 'hoursWorked' ? parseFloat(value) || 0 : value };
            const updatedEntries = [...entries];
            updatedEntries[index] = updatedEntry;
            return {
                ...prevState,
                [projectId]: {
                    ...prevState[projectId],
                    weeks: {
                        ...prevState[projectId].weeks,
                        [weekKey]: {
                            ...prevState[projectId].weeks[weekKey],
                            entries: updatedEntries
                        }
                    }
                }
            };
        });
    }, []);

    const fetchAndGroupTimesheets = async () => {
        setLoading(true);
        try {
            const url = payrollCycleId
                ? `/ts/api/timesheets/payroll/${payrollCycleId}`
                : "/ts/api/timesheets";
            const response = await api_data.get(url);
            const grouped = groupTimesheetsByProjectAndWeek(response.data.timesheets);
            setMonthlyTimesheet(response.data);
            setGroupedTimesheets(grouped);

            const projectsList = Object.keys(grouped).map(projectId => ({
                id: projectId,
                name: grouped[projectId].projectname
            }));
            setProjects(projectsList);

            if (selectedProject === 'all' && projectsList.length > 0) {
                setSelectedProject(projectsList[0].id);
            }
        } catch (error) {
            console.error("Error fetching timesheets:", error);
            if (error.response?.status === 403) {
                setNotRegistered(true);
            } else if (error.response?.status === 401) {
                logout();
                navigate("/login");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitTimesheet = async () => {
        try {
            const response = await api_data.put(`/ts/api/timesheets/submit`, {
                timesheetId: monthlyTimesheet.timesheetId,
                payrollCycleId: monthlyTimesheet.payrollId,
            });
            setMonthlyTimesheet(prevState => ({
                ...prevState,
                status: response.data
            }));
            setIsEditing(false);
            toast.success('Timesheet submitted');
        } catch (error) {
            console.error("Error submitting timesheet:", error);
            toast.error('Failed to submit timesheet');
        }
    };

    const getStatusInfo = (status) => {
        if (isDraftStatus(status)) {
            return { text: 'Draft', className: 'badge-draft' };
        }
        if (status.toLowerCase() === "approved") {
            return { text: 'Approved', className: 'badge-approved' };
        }
        return { text: status, className: 'badge-pending' };
    };

    const statusInfo = getStatusInfo(monthlyTimesheet.status);

    const renderWeeklyEntries = (weeks, projectId) => (
        Object.entries(weeks)
            .sort(([weekA], [weekB]) => new Date(weekB) - new Date(weekA))
            .map(([weekKey, weekData]) => (
                <tbody key={weekKey} className="divide-y divide-theme-border-subtle">
                    <tr className="timesheet-week-title">
                        <td colSpan={2}>
                            Week of {new Date(weekKey).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                            })}
                            <span className="ml-2 font-normal text-theme-muted">· {weekData.weekTotal}h</span>
                        </td>
                    </tr>
                    {weekData.entries
                        .sort((a, b) => new Date(a.workSegmentDate) - new Date(b.workSegmentDate))
                        .map((entry, index) => (
                            <TimesheetRow
                                key={`${weekKey}-${index}`}
                                entry={entry}
                                index={index}
                                projectId={projectId}
                                weekKey={weekKey}
                                isEditing={isEditing && canEdit}
                                onChange={handleInputChange}
                            />
                        ))}
                </tbody>
            ))
    );

    if (notRegistered) return <NotRegistered />;

    const monthTitle = getMonthTitle(monthlyTimesheet.timesheets);
    const source = isEditing && canEdit ? editableTimesheets : groupedTimesheets;
    const projectData = selectedProject !== 'all' ? source[selectedProject] : null;
    const showActions = canEdit && projectData && !loading;

    return (
        <div className="page-shell pb-24">
            <div className="page-container">
                <header className="mb-4">
                    <h1 className="text-xl font-bold text-theme-text sm:text-2xl">{monthTitle}</h1>
                    {projectData && (
                        <p className="mt-1 text-sm text-theme-muted">
                            {projectData.totalHours}h total · <span className={statusInfo.className}>{statusInfo.text}</span>
                        </p>
                    )}
                </header>

                {projects.length > 1 && (
                    <div className="timesheet-toolbar">
                        <span className="text-sm font-medium text-theme-muted">Project</span>
                        <div className="flex flex-wrap gap-2">
                            {projects.map(project => (
                                <button
                                    key={project.id}
                                    type="button"
                                    onClick={() => setSelectedProject(project.id)}
                                    className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                                        selectedProject === project.id
                                            ? 'bg-brand-600 text-white'
                                            : 'bg-theme-surface-muted text-theme-text-secondary hover:bg-theme-surface-hover'
                                    }`}
                                >
                                    {project.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {projects.length === 1 && (
                    <div className="timesheet-toolbar">
                        <span className="text-sm font-medium text-theme-text">{projects[0].name}</span>
                    </div>
                )}

                {loading ? (
                    <div className="surface-card animate-pulse p-8">
                        <div className="space-y-3">
                            <div className="h-8 rounded bg-theme-skeleton" />
                            <div className="h-8 rounded bg-theme-skeleton" />
                            <div className="h-8 rounded bg-theme-skeleton" />
                        </div>
                    </div>
                ) : !projectData ? (
                    <div className="surface-card py-12 text-center text-theme-muted">
                        No timesheet entries for this period.
                    </div>
                ) : (
                    <div className="surface-card overflow-hidden">
                        <table className="min-w-full">
                            <thead>
                                <tr className="border-b border-theme-border bg-theme-table-head">
                                    <th className="py-2.5 pl-4 pr-3 text-left text-xs font-medium text-theme-text-secondary">Day</th>
                                    <th className="py-2.5 px-3 text-right text-xs font-medium text-theme-text-secondary">Hours</th>
                                </tr>
                            </thead>
                            {renderWeeklyEntries(projectData.weeks, selectedProject)}
                        </table>
                    </div>
                )}

                {!canEdit && projectData && (
                    <p className="mt-4 text-center text-sm text-theme-muted">
                        This timesheet is {statusInfo.text.toLowerCase()} and can no longer be edited.
                    </p>
                )}
            </div>

            {showActions && (
                <div className="timesheet-sticky-actions">
                    <div className="mx-auto flex max-w-3xl gap-3 px-4 sm:px-6 lg:px-8">
                        <button onClick={handleSaveAll} className="btn-success flex-1">
                            <FiSave className="h-4 w-4" />
                            Save
                        </button>
                        <button onClick={handleSubmitTimesheet} className="btn-primary flex-1">
                            <FiSend className="h-4 w-4" />
                            Submit
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const TimesheetTable = () => <TimesheetProjectView />;

export default TimesheetTable;
